import { forwardRef, type JSX, useMemo } from 'react';
import { Div, Flex } from '@fastnorth/kraken/components/layout';
import { useApp } from '../../../../../../../../hooks/AppProvider.tsx';
import { useSubEvent } from '../../../../../../../../hooks/SubEventProvider.tsx';
import { useParticipantDetails } from '../../../../hooks/ParticipantDetailsProvider.tsx';
import { type FinisherCertificate, type FullSubEvent } from '@fastnorth/timer-api';
import { useTranslation } from 'react-i18next';
import Separator from '@fastnorth/kraken/components/separator';
import { Text, H2 } from '@fastnorth/kraken/components/typography';
import type {
  ResultDetailParticipant,
  SegmentResult,
} from '../../../../../../../../graphql/rest-link/types.ts';
import normalizeImageUrls from '../../../../../../../../utils/normalizeImageUrls.ts';
import FormatOrdinal from '../utils/FormatOrdinal.tsx';
import getVirtualChallengePrimaryStatLabelKey from './utils/getVirtualChallengePrimaryStatLabelKey.ts';

export type DefaultFinisherCertificateProps = {
  certificate?: FinisherCertificate;
};

const DefaultFinisherCertificate = forwardRef<HTMLDivElement, DefaultFinisherCertificateProps>(
  ({ certificate }, ref): JSX.Element | null => {
    const { t } = useTranslation('translation', {
      keyPrefix: 'karwa.components.participant_details.finisher_certificate',
    });
    const [{ event }] = useApp();
    const { subEvent } = useSubEvent();
    const [{ resultDetail }] = useParticipantDetails();

    const {
      participant = {} as ResultDetailParticipant,
      stats,
      result,
      segmentResults,
    } = resultDetail || {};

    const segmentResultsById = useMemo(() => {
      if (!segmentResults) return new Map<string, SegmentResult>();
      return segmentResults.reduce((acc, curr) => {
        acc.set(curr.segmentResultUuid, curr);
        return acc;
      }, new Map<string, SegmentResult>());
    }, [segmentResults]);

    if (!event || !subEvent || !resultDetail || !certificate || !result) {
      return null;
    }

    const {
      isEventLogoVisible,
      isProviderLogoVisible,
      sponsorLogos = [],
      sponsorLogoHeight,
    } = certificate;

    const primaryTimeStat = stats?.primary?.[0]?.value;
    const rankingStats = (stats?.ranking ?? []) as Record<string, string>[];
    const { segments } = result;

    // event fields
    const { styles, provider } = event;
    const showLogo = isEventLogoVisible || isProviderLogoVisible;

    //subEvent fields
    const { settings = {} as FullSubEvent['settings'], resultsType, challengeSettings } = subEvent;
    const { isRelayEnabled } = settings;
    const primaryStatLabelKey = getVirtualChallengePrimaryStatLabelKey({
      resultsType,
      challengeType: challengeSettings?.type,
    });

    //prefer event logo
    const eventLogoUrl = isEventLogoVisible ? styles.logo : null;
    const providerLogoUrl = isProviderLogoVisible ? (provider?.logo ?? null) : null;
    const visibleLogoUrl = eventLogoUrl || providerLogoUrl;
    const logoAltText = eventLogoUrl ? 'event_logo_alt_text' : 'provider_logo_alt_text';

    const name = isRelayEnabled ? participant.team : (participant.displayName ?? participant.name);

    return (
      <Flex ref={ref} className="flex-col gap-4 justify-center items-center h-full w-full">
        {/* Logo */}
        {showLogo && !!visibleLogoUrl && (
          <Div
            data-testid={'event-logo'}
            aria-label={t(logoAltText)}
            className="w-[150px] h-[150px] min-w-[110px] max-h-[100px] lg:min-h-[125px]"
            style={{
              background: `url("${normalizeImageUrls(visibleLogoUrl)}")`,
              backgroundSize: 'contain',
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'center center',
            }}
          />
        )}
        <Text size={'sm'}>{t('default.certifies_that')}</Text>
        <H2 modifier={'pretty'} size={'3xl'} fontWeight={'bold'} spacing={'0 0 7 0'}>
          <Div style={{ fontSize: '2em' }}>{name}</Div>
        </H2>
        <Text size={'sm'}>
          {t('default.completed', {
            raceName: (
              <Text key={'cert-sub-event-name'} size={'lg'} fontWeight={'body'}>
                {subEvent.name}
              </Text>
            ),
          })}
        </Text>
        <Text size={'sm'}>{t(primaryStatLabelKey)}</Text>
        <Text size={'3xl'} fontWeight={'semibold'}>
          {primaryTimeStat || t('default.time_unavailable')}
        </Text>

        <Div className={'w-1/6'} spacing={'6'}>
          <Separator variant={'dashed'} />
        </Div>

        {/* Stats */}
        {rankingStats.length > 0 && (
          <Flex className="items-center justify-center gap-11">
            {rankingStats.map(({ value, label }, idx) => {
              return (
                <Flex className="flex-col gap-3 items-center" key={idx}>
                  <Text size={'lg'} fontWeight={'semibold'}>
                    <FormatOrdinal value={value} />
                  </Text>
                  <Text size={'xl'}>{label}</Text>
                </Flex>
              );
            })}
          </Flex>
        )}

        {/* Segments for relay */}
        {isRelayEnabled && Array.isArray(segments) && segments.length > 0 && (
          <Flex className="flex-row items-center gap-7" spacing={'6 0 0 0'}>
            {segments.map((segment, idx) => {
              const segmentResult = segmentResultsById.get(segment.segmentResultUuid);
              if (!segment || !segmentResult?.label || !segment.splitTime) return null;
              return (
                <Flex className="items-center flex-col" key={idx}>
                  {segment.participantName && (
                    <Text size={'md'} fontWeight={'bold'}>
                      {segment.participantName}
                    </Text>
                  )}
                  <Div className={`${isRelayEnabled ? 'text-sm' : 'text-md font-semibold'}`}>
                    {segmentResult.label ? <Text>{segmentResult.label} </Text> : null}
                    {segment.distance != null && <Text size={'lg'}>{segment.distance}</Text>}
                  </Div>
                  <Div className={'text-sm'}>{segment.splitTimeFormatted || segment.splitTime}</Div>
                </Flex>
              );
            })}
          </Flex>
        )}

        {/* Sponsor logos */}
        {sponsorLogos.length > 0 && (
          <Flex spacing={'7 0 0 0'} className="flex-row items-center justify-evenly w-full">
            {sponsorLogos.map(({ id, imagePath }) => (
              <Div
                key={id}
                data-testid={'sponsor-logo'}
                aria-label={t(logoAltText)}
                style={{
                  width: sponsorLogoHeight,
                  height: sponsorLogoHeight,
                  background: `url("${normalizeImageUrls(imagePath)}")`,
                  backgroundSize: 'contain',
                  backgroundRepeat: 'no-repeat',
                  backgroundPosition: 'center center',
                }}
              />
            ))}
          </Flex>
        )}
      </Flex>
    );
  }
);

DefaultFinisherCertificate.displayName = 'DefaultFinisherCertificate';

export default DefaultFinisherCertificate;
